import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowUpRight, CheckCircle2, AlertTriangle, Wrench, Clock } from 'lucide-react';
import SEO from '../components/SEO';

// =============================================================================
// GIWAHS — Public Status Page (review-only)
// Static placeholder structure. Wires to a real monitoring source only after
// founder approval. No backend calls; values are illustrative.
// =============================================================================

const COMPONENTS = [
  { key: 'web',     label: 'Marketing site',            status: 'operational', desc: 'Public marketing surfaces (giwahs.com / *.giwahs.com)' },
  { key: 'app',     label: 'Buyer & supplier app',      status: 'operational', desc: 'Authenticated buyer and supplier experiences' },
  { key: 'api',     label: 'Backend API',               status: 'operational', desc: 'FastAPI services behind /api/*' },
  { key: 'db',      label: 'Database (Supabase)',       status: 'operational', desc: 'Primary Postgres + RLS policies + audit log' },
  { key: 'auth',    label: 'Authentication',            status: 'operational', desc: 'Supabase Auth (sign-in, sessions, password reset)' },
  { key: 'pay',     label: 'Payments (Stripe)',         status: 'operational', desc: 'Subscription processing, Customer Portal, Stripe Tax' },
  { key: 'email',   label: 'Email dispatch (Resend)',   status: 'operational', desc: 'Transactional email + lifecycle notifications' },
  { key: 'edge',    label: 'Edge & CDN (Cloudflare)',   status: 'operational', desc: 'CDN, WAF, DDoS mitigation, bot management' },
  { key: 'cron',    label: 'Membership cron',           status: 'planned',     desc: 'Renewal + verification reminder dispatcher (post-activation)' },
];

const UPTIME_30D = [
  { label: 'Current status',                 value: 'All systems operational', hint: '8 of 8 components' },
  { label: 'Customer-impacting incidents',   value: '0',                       hint: 'Since public launch' },
  { label: 'Public uptime reporting',        value: 'Launching',               hint: '30 / 90-day and 12-month windows' },
];

const INCIDENTS = [
  // Empty by design. The structure below is what each incident card will use.
  // {
  //   id: 'INC-0001',
  //   title: 'Example: brief degradation of email dispatch',
  //   started_at: '2026-02-01T10:42:00Z',
  //   resolved_at: '2026-02-01T11:14:00Z',
  //   severity: 'SEV-3',
  //   components: ['email'],
  //   summary: '…',
  //   updates: [{ts:'…', body:'…'}, …],
  // },
];

const MAINTENANCE = [
  // Empty by design. The structure below is what each scheduled maintenance card will use.
  // {
  //   id: 'MNT-0001',
  //   title: 'Example: rolling DB upgrade',
  //   window_start: '…', window_end: '…',
  //   components: ['db'], impact: 'minimal-to-none',
  //   notes: '…',
  // },
];

const STATUS_PILL = {
  operational: { label: 'Operational',         tone: 'border-emerald-700 bg-emerald-50 text-emerald-900',  icon: CheckCircle2 },
  degraded:    { label: 'Degraded performance', tone: 'border-amber-700 bg-amber-50 text-amber-900',         icon: AlertTriangle },
  partial:     { label: 'Partial outage',       tone: 'border-amber-800 bg-amber-50 text-amber-900',          icon: AlertTriangle },
  outage:      { label: 'Major outage',         tone: 'border-red-800 bg-red-50 text-red-900',                icon: AlertTriangle },
  maint:       { label: 'Maintenance',          tone: 'border-blue-700 bg-blue-50 text-blue-900',             icon: Wrench },
  planned:     { label: 'Planned',              tone: 'border-neutral-700 bg-neutral-50 text-neutral-700',    icon: Clock },
};

const StatusPill = ({ status }) => {
  const s = STATUS_PILL[status] || STATUS_PILL.operational;
  const Icon = s.icon;
  return (
    <span
      data-testid={`status-pill-${status}`}
      className={`inline-flex items-center gap-1.5 rounded-full border px-3 py-1 text-xs uppercase tracking-wider font-mono ${s.tone}`}
    >
      <Icon size={12} strokeWidth={1.7} />
      {s.label}
    </span>
  );
};

const Status = () => {
  const breadcrumbs = [
    { name: 'Home', url: '/' },
    { name: 'Status', url: '/status' },
  ];
  const updatedAt = new Date().toISOString().split('T')[0];

  return (
    <div data-testid="status-page" className="surface-obsidian text-platinum">
      <SEO
        title="Status — System & Incident History"
        description="GIWAHS public status page. Component status, planned uptime reporting, incident history, and scheduled maintenance for the GIWAHS platform."
        breadcrumbs={breadcrumbs}
      />

      {/* Hero */}
      <section className="border-b border-hairline surface-obsidian">
        <div className="mx-auto max-w-[1440px] px-5 py-14 md:px-8 md:py-20">
          <Link to="/trust" className="text-xs uppercase tracking-wider text-neutral-500 font-mono hover:text-neutral-900">
            ← Trust Centre
          </Link>
          <div className="mt-6 flex flex-wrap items-baseline justify-between gap-4">
            <div>
              <span className="label-overline">/ Status</span>
              <h1
                className="mt-4 font-display tracking-tighter"
                style={{ fontWeight: 700, fontSize: 'clamp(2.25rem, 6vw, 4.5rem)', lineHeight: 0.95 }}
              >
                All systems operational.
              </h1>
            </div>
            <div data-testid="status-overall-pill" className="inline-flex items-center gap-2">
              <StatusPill status="operational" />
            </div>
          </div>
          <p className="mt-6 max-w-2xl text-base leading-relaxed text-neutral-700">
            All GIWAHS platform components are operational. This page is the public record of
            component status, incident history, and scheduled maintenance — automated uptime
            metrics publish here as our public monitoring programme rolls out.
          </p>
          <p className="mt-4 text-xs uppercase tracking-wider text-neutral-500 font-mono">
            Page generated: {updatedAt} · Updated with every platform release
          </p>
        </div>
      </section>

      {/* Components */}
      <section className="border-b border-hairline surface-obsidian-2">
        <div className="mx-auto max-w-[1440px] px-5 py-12 md:px-8 md:py-16">
          <span className="label-overline">/ 01 — Component status</span>
          <h2 className="mt-3 font-display text-2xl tracking-tighter md:text-3xl" style={{ fontWeight: 600 }}>
            Live component breakdown.
          </h2>

          <div data-testid="status-components" className="mt-8 divide-y divide-neutral-200 border border-neutral-900 bg-white">
            {COMPONENTS.map((c) => (
              <div
                key={c.key}
                data-testid={`status-component-${c.key}`}
                className="flex flex-col gap-3 p-5 md:flex-row md:items-center md:justify-between"
              >
                <div>
                  <div className="font-medium">{c.label}</div>
                  <div className="mt-1 text-sm text-neutral-600">{c.desc}</div>
                </div>
                <StatusPill status={c.status} />
              </div>
            ))}
          </div>

          <p className="mt-4 text-xs text-neutral-500 font-mono">
            Note: <code>Membership cron</code> is marked <em>Planned</em> until activation is approved.
          </p>
        </div>
      </section>

      {/* Uptime */}
      <section className="border-b border-hairline surface-obsidian">
        <div className="mx-auto max-w-[1440px] px-5 py-12 md:px-8 md:py-16">
          <span className="label-overline">/ 02 — Uptime reporting (planned)</span>
          <h2 className="mt-3 font-display text-2xl tracking-tighter md:text-3xl" style={{ fontWeight: 600 }}>
            Reporting structure ready, values pending.
          </h2>

          <div className="mt-8 grid gap-px bg-black md:grid-cols-3">
            {UPTIME_30D.map((u) => (
              <div
                key={u.label}
                data-testid={`status-uptime-${u.label.replace(/\s+/g, '-').toLowerCase()}`}
                className="bg-white p-6"
              >
                <div className="label-overline tracking-wider">{u.label}</div>
                <div className="mt-3 font-display text-3xl tracking-tighter" style={{ fontWeight: 700 }}>
                  {u.value}
                </div>
                <div className="mt-2 text-xs text-neutral-500 font-mono">{u.hint}</div>
              </div>
            ))}
          </div>

          <div className="mt-6 text-sm leading-relaxed text-neutral-700">
            As public monitoring launches, this section will publish: (a) rolling 30/90/365-day
            uptime, calculated as <code className="bg-neutral-100 px-1 py-0.5">customer-impacting up-minutes /
            total in-window minutes</code>; (b) per-component availability for each of the
            components listed in section 01; (c) a 30-day sparkline strip for each component.
          </div>
        </div>
      </section>

      {/* Incident history */}
      <section className="border-b border-hairline surface-obsidian-2">
        <div className="mx-auto max-w-[1440px] px-5 py-12 md:px-8 md:py-16">
          <span className="label-overline">/ 03 — Incident history</span>
          <h2 className="mt-3 font-display text-2xl tracking-tighter md:text-3xl" style={{ fontWeight: 600 }}>
            12-month rolling window.
          </h2>

          {INCIDENTS.length === 0 ? (
            <div
              data-testid="status-incidents-empty"
              className="mt-8 border border-neutral-900 bg-white p-8 text-center"
            >
              <CheckCircle2 size={28} strokeWidth={1.5} className="mx-auto text-emerald-700" />
              <div className="mt-4 font-display text-xl tracking-tight" style={{ fontWeight: 600 }}>
                No customer-impacting incidents recorded.
              </div>
              <p className="mt-2 text-sm text-neutral-600">
                When an incident occurs, a card will appear here with: ID, started/resolved
                timestamps, severity (SEV-1 to SEV-4, per our Security & Incident Response Policy), components affected,
                summary, and timeline updates.
              </p>
            </div>
          ) : (
            <ul data-testid="status-incidents" className="mt-8 space-y-4">
              {INCIDENTS.map((i) => (
                <li key={i.id} className="border border-neutral-900 bg-white p-5">
                  <div className="text-xs uppercase tracking-wider text-neutral-500 font-mono">{i.id} · {i.severity}</div>
                  <div className="mt-2 font-medium">{i.title}</div>
                  <div className="mt-1 text-sm text-neutral-600">{i.summary}</div>
                </li>
              ))}
            </ul>
          )}

          <p className="mt-4 text-xs text-neutral-500 font-mono">
            Severity model: SEV-1 (confirmed breach / prod down) → SEV-4 (informational) — see{' '}
            <Link to="/trust/security" className="underline">Trust · Security</Link>.
          </p>
        </div>
      </section>

      {/* Maintenance */}
      <section className="border-b border-hairline surface-obsidian">
        <div className="mx-auto max-w-[1440px] px-5 py-12 md:px-8 md:py-16">
          <span className="label-overline">/ 04 — Scheduled maintenance</span>
          <h2 className="mt-3 font-display text-2xl tracking-tighter md:text-3xl" style={{ fontWeight: 600 }}>
            Notices, with advance warning.
          </h2>

          {MAINTENANCE.length === 0 ? (
            <div
              data-testid="status-maintenance-empty"
              className="mt-8 border border-dashed border-neutral-400 bg-white p-8 text-center"
            >
              <Wrench size={24} strokeWidth={1.5} className="mx-auto text-neutral-500" />
              <div className="mt-4 font-display text-lg tracking-tight" style={{ fontWeight: 600 }}>
                No scheduled maintenance.
              </div>
              <p className="mt-2 text-sm text-neutral-600">
                Customer-impacting maintenance windows will be posted here at least 7 days in
                advance and will repeat 24 hours before the window opens.
              </p>
            </div>
          ) : (
            <ul data-testid="status-maintenance" className="mt-8 space-y-4">
              {MAINTENANCE.map((m) => (
                <li key={m.id} className="border border-neutral-900 bg-white p-5">
                  <div className="text-xs uppercase tracking-wider text-neutral-500 font-mono">{m.id} · scheduled</div>
                  <div className="mt-2 font-medium">{m.title}</div>
                </li>
              ))}
            </ul>
          )}
        </div>
      </section>

      {/* Subscribe + cross-links */}
      <section className="border-b border-black bg-[#0A0A0A] text-white">
        <div className="mx-auto grid max-w-[1440px] gap-10 px-5 py-12 md:grid-cols-2 md:px-8 md:py-16">
          <div>
            <span className="label-overline text-neutral-400">/ 05 — Subscribe to status updates</span>
            <h3 className="mt-3 font-display text-2xl tracking-tight" style={{ fontWeight: 600 }}>
              Get notified of incidents and maintenance.
            </h3>
            <p className="mt-4 max-w-md text-sm leading-relaxed text-neutral-300">
              Email{' '}
              <a href="mailto:status@giwahs.com" className="underline">status@giwahs.com</a> to be added to the announce list.
            </p>
            <a
              data-testid="status-subscribe-cta"
              href="mailto:status@giwahs.com?subject=Status%20updates%20subscribe"
              className="mt-6 inline-flex items-center gap-2 border-b border-white pb-1 text-sm font-medium hover:gap-3 transition-all"
            >
              Email status@giwahs.com <ArrowUpRight size={14} strokeWidth={1.5} />
            </a>
          </div>
          <div>
            <span className="label-overline text-neutral-400">/ See also</span>
            <ul className="mt-3 space-y-2 text-sm text-neutral-300">
              <li><Link to="/trust" className="underline">Trust Centre</Link></li>
              <li><Link to="/trust/security" className="underline">Security policy</Link></li>
              <li><Link to="/trust/privacy" className="underline">Privacy</Link></li>
              <li><Link to="/trust/compliance" className="underline">Compliance</Link></li>
            </ul>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Status;
